import type { FC } from 'react'
import React, { memo, useMemo, useState, useEffect } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { Table } from '@homeviews/shared-ui'
import { isEqual } from '@homeviews/utils'
import { cloneDeep } from 'lodash'

import { useGetAwardsStatsQuery } from '@homeviews/store'

import {
  createRows,
  headCells,
  getAwardsTableMetaData,
  filterAwardsData,
} from '../../helpers/transform'
import PillFilter from '../PillFilter'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      '& .MuiTableCell-head': {
        textAlign: 'center',
        '&:first-child': {
          textAlign: 'left',
        },
      },
    },
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      marginLeft: 'auto',
    },
    filtersWrapper: {
      display: 'flex',
      marginBottom: 19,
    },
  }),
)
interface IFilter {
  value: string
  label: string
}
interface IProps {
  activeFilters: any
  graphDisabled?: boolean
  gridRef?: HTMLElement
}

const AwardsTable: FC<IProps> = memo(
  ({ activeFilters, graphDisabled }) => {
    const classes = useStyles()

    const { data: awards } = useGetAwardsStatsQuery(
      { ...activeFilters },
      {
        skip: graphDisabled,
      },
    )
    const [filteredData, setFilteredData] = useState<any>([])
    const [dateFilter, setDateFilter] = useState<string>('')

    useEffect(() => {
      const newFilteredData = filterAwardsData(
        cloneDeep(awards?.data),
        dateFilter,
      )

      setFilteredData(newFilteredData)
    }, [dateFilter, awards])

    const filters: any = useMemo(
      () => getAwardsTableMetaData(awards?.data),
      [awards?.data],
    )

    useEffect(() => {
      if (!dateFilter && filters?.[0]?.value) {
        filterData(filters[0].value)
      }
    }, [filters, dateFilter])

    const rows = useMemo(() => createRows(filteredData), [filteredData])

    const filterData = (value: string) => {
      setDateFilter(value)
    }

    return (
      <div className={classes.root}>
        {!!filters?.length && (
          <div className={classes.filtersWrapper}>
            <div className={classes.filters}>
              {filters.map((filter: IFilter) => {
                return (
                  <PillFilter
                    key={filter.value}
                    active={dateFilter === filter.value}
                    value={filter.value}
                    label={filter.label}
                    onClick={() => filterData(filter.value)}
                  />
                )
              })}
            </div>
          </div>
        )}
        <Table headCells={headCells} rows={rows} fontStyle="small" />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default AwardsTable
